import React from "react";

import {
  Link as ExternalLink,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIconProps,
  Tooltip,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";

interface CollapsibleNavItemProps {
  icon: React.ReactElement<SvgIconProps>;
  label: string;
  /** When collapsed, the label is hidden and shown as a right-placed tooltip. */
  collapsed: boolean;
  /** Internal router destination. */
  to?: string;
  /** External destination (opens in a new tab). */
  href?: string;
  /** Fired when the item is neither an internal nor external link. */
  onClick?: () => void;
  dataTestId?: string;
  /**
   * Overrides the tooltip text (e.g. to include a keyboard shortcut hint) and
   * shows it in both collapsed and expanded states. Without this, the plain
   * label is only shown as a tooltip while collapsed.
   */
  tooltip?: string;
}

/**
 * A single sidenav entry that stays centered, hides its label, shows a tooltip
 * and highlights just the icon when the drawer is collapsed. Renders as a
 * router link (`to`), an external link (`href`), or a plain button (`onClick`).
 */
const CollapsibleNavItem = ({
  icon,
  label,
  collapsed,
  to,
  href,
  onClick,
  dataTestId,
  tooltip,
}: CollapsibleNavItemProps) => {
  const { pathname } = useLocation();
  // Only routed items (`to`) can be "active". "/" matches exactly; every other
  // route matches itself or any nested path beneath it.
  const isActive = to
    ? to === "/"
      ? pathname === "/"
      : pathname === to || pathname.startsWith(`${to}/`)
    : false;

  const buttonSx = {
    minHeight: collapsed ? 40 : 28,
    justifyContent: collapsed ? "center" : "initial",
    px: collapsed ? 0 : 2,
    mx: collapsed ? "auto" : 1,
    // Vertical gap so adjacent hover/selected pills never touch.
    mb: 0.5,
    borderRadius: 2,
    ...(collapsed && { width: 40 }),
    color: isActive ? "primary.main" : "text.secondary",
    bgcolor: isActive ? "rgba(255, 255, 255, 0.08)" : "transparent",
    "& .MuiListItemIcon-root": {
      color: isActive ? "primary.main" : "text.secondary",
    },
    "&:hover": {
      bgcolor: isActive
        ? "rgba(255, 255, 255, 0.12)"
        : "rgba(255, 255, 255, 0.06)",
    },
  };

  const content = (
    <>
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: "center",
        }}
      >
        {collapsed ? icon : React.cloneElement(icon, { fontSize: "small" })}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
        />
      )}
    </>
  );

  const commonProps = {
    // When collapsed the label text is hidden, so expose it as the accessible
    // name. When expanded the visible ListItemText already provides the name
    // — unless a custom tooltip (e.g. a shortcut hint) adds context screen
    // readers wouldn't otherwise get from hovering.
    "aria-label": tooltip ?? (collapsed ? label : undefined),
    "data-testid": dataTestId,
    sx: buttonSx,
  };

  let button: React.ReactNode;
  if (to) {
    button = (
      <ListItemButton
        component={Link}
        to={to}
        className="remove-visited-link-style"
        {...commonProps}
      >
        {content}
      </ListItemButton>
    );
  } else if (href) {
    button = (
      <ListItemButton
        component={ExternalLink}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="remove-visited-link-style"
        {...commonProps}
      >
        {content}
      </ListItemButton>
    );
  } else {
    button = (
      <ListItemButton onClick={onClick} {...commonProps}>
        {content}
      </ListItemButton>
    );
  }

  return (
    <ListItem disablePadding sx={{ display: "block" }}>
      {collapsed || tooltip ? (
        <Tooltip title={tooltip ?? label} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

export default CollapsibleNavItem;
